import { decodeWaveform } from "@/lib/audio-waveform";
import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import type { ArenaSample } from "./data/types";
import { resolveAudioUrl } from "./data/resolveAudioUrl";

type Props = {
  side: "left" | "right";
  sample: ArenaSample;
  isActive: boolean;
  onPlay: () => void;
  onListened: () => void;
  onEnded?: () => void;
};

const LISTEN_GATE_SECONDS = 3;
const BAR_COUNT = 100;

// Subtle placeholder while waveform decodes — low uniform height, not fake amplitude
const PLACEHOLDER_BARS = Float32Array.from({ length: BAR_COUNT }, () => 0.15);

export const AudioCard = forwardRef<{ play: () => void }, Props>(function AudioCard({ side, sample, isActive, onPlay, onListened, onEnded }, ref) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);
  const playAttempt = useRef(0);
  const pendingPlay = useRef(false);
  const currentPath = useRef(sample.audioPath);
  currentPath.current = sample.audioPath;
  const [retry, setRetry] = useState(0);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [waveformError, setWaveformError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);           // 0–1 playback position
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [waveform, setWaveform] = useState<Float32Array>(PLACEHOLDER_BARS); // always has bars
  const [audioUrl, setAudioUrl] = useState<string | null>(null); // resolved (presigned) URL

  useImperativeHandle(ref, () => ({ play: () => { void startPlayback(); } }));

  const requiredSeconds = Math.min(LISTEN_GATE_SECONDS, Math.max(1, sample.durationMs / 1000));
  const label = side === "left" ? "A" : "B";
  const gateProgress = Math.min(1, playedSeconds / requiredSeconds);
  const gateRemaining = Math.max(0, Math.ceil(requiredSeconds - playedSeconds));
  const gateMet = gateProgress >= 1;

  // Reset all playback state when the sample changes (next pair)
  useEffect(() => {
    playAttempt.current++;
    pendingPlay.current = false;
    setPlayedSeconds(0);
    setProgress(0);
    setIsPlaying(false);
    stopTick();
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
  }, [sample.audioPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the manifest path to a playable URL (presigned for private S3 keys)
  useEffect(() => {
    let cancelled = false;
    setAudioUrl(null);
    setPlaybackError(null);
    setWaveformError(false);
    resolveAudioUrl(sample.audioPath)
      .then((url) => { if (!cancelled) setAudioUrl(url); })
      .catch((cause) => { if (!cancelled) setPlaybackError(cause instanceof Error ? cause.message : "Audio could not be loaded"); });
    return () => { cancelled = true; };
  }, [sample.audioPath, retry]);

  // Decode real waveform from the resolved URL — replaces placeholder, no
  // flicker since the placeholder is always visible.
  useEffect(() => {
    if (!audioUrl) return;
    let cancelled = false;
    setWaveform(PLACEHOLDER_BARS);
    decodeWaveform(audioUrl, BAR_COUNT)
      .then((bars) => { if (!cancelled) setWaveform(bars); })
      .catch(() => { if (!cancelled) setWaveformError(true); });
    return () => { cancelled = true; };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || isActive) return;
    playAttempt.current++;
    pendingPlay.current = false;
    audio.pause();
    setIsPlaying(false);
  }, [isActive]);

  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress(audio.currentTime / audio.duration);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const startTick = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const stopTick = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  function handleTimeUpdate() {
    const audio = audioRef.current;
    if (!audio) return;
    const played = Math.max(playedSeconds, audio.currentTime);
    setPlayedSeconds(played);
    if (played >= requiredSeconds || audio.ended) onListened();
  }

  async function startPlayback() {
    const audio = audioRef.current;
    if (!audio || !audioUrl || pendingPlay.current || !audio.paused) return;
    const path = sample.audioPath;
    const attempt = ++playAttempt.current;
    pendingPlay.current = true;
    setPlaybackError(null);
    setIsPlaying(true);
    onPlay();
    try {
      if (audio.error) audio.load();
      await audio.play();
      if (attempt !== playAttempt.current || currentPath.current !== path || audioRef.current !== audio) return;
      setIsPlaying(true);
      startTick();
    } catch (cause) {
      if (attempt !== playAttempt.current || currentPath.current !== path || audioRef.current !== audio) return;
      setIsPlaying(false);
      if (!((cause instanceof Error || cause instanceof DOMException) && cause.name === "AbortError")) {
        setPlaybackError("Playback could not start. Press play to try again.");
      }
    } finally {
      if (attempt === playAttempt.current) pendingPlay.current = false;
    }
  }

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    if (pendingPlay.current || !audio.paused) {
      playAttempt.current++;
      pendingPlay.current = false;
      audio.pause();
      setIsPlaying(false);
      stopTick();
      return;
    }
    await startPlayback();
  }

  function handleWaveformClick(e: React.MouseEvent<HTMLDivElement>) {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio);
  }

  return (
    <motion.div
      className={`tts-audio-card ${isActive ? "tts-audio-card--active" : ""}`}
      aria-label={`Option ${label}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut", delay: side === "left" ? 0 : 0.06 }}
    >
      <div className="tts-card-header">
        <div className="tts-side-badge">
          <div className={`tts-side-badge-dot ${isActive ? "active" : ""}`} />
          Option {label}
        </div>
        <span className="tts-blind-chip">Identity hidden</span>
      </div>

      <div className="tts-player-row">
        <button
          className="tts-play-btn"
          type="button"
          aria-label={`${isPlaying ? "Pause" : "Play"} option ${label}`}
          disabled={!audioUrl}
          onClick={() => void togglePlayback()}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: 2 }} />}
        </button>

        <div
          className="tts-waveform-wrap"
          onClick={handleWaveformClick}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <div className="tts-wbars">
            {Array.from(waveform).map((amp, i) => {
              const played = (i / BAR_COUNT) < progress;
              return (
                <div
                  key={i}
                  className={`tts-wbar ${played ? (isActive ? "tts-wbar--played" : "tts-wbar--played-inactive") : "tts-wbar--unplayed"}`}
                  style={{ height: `${Math.max(8, amp * 100)}%` }}
                />
              );
            })}
          </div>
          <div className="tts-playhead" style={{ left: `${progress * 100}%` }} />
        </div>
      </div>

      {playbackError && <p role="alert" className="text-xs text-destructive">{playbackError}{!audioUrl && <button type="button" className="ml-2 underline" onClick={() => setRetry((value) => value + 1)}>Retry audio</button>}</p>}
      {waveformError && !playbackError && <p className="text-xs text-muted-foreground">Waveform unavailable; audio can still be played.</p>}
      <audio
        key={sample.audioPath}
        ref={audioRef}
        src={audioUrl ?? undefined}
        onError={(event) => { if (event.currentTarget === audioRef.current && currentPath.current === sample.audioPath) setPlaybackError("Audio could not be played. Press play to retry."); }}
        onPlay={(event) => { if (event.currentTarget !== audioRef.current || currentPath.current !== sample.audioPath) return; onPlay(); setIsPlaying(true); startTick(); }}
        onPause={(event) => { if (event.currentTarget !== audioRef.current || currentPath.current !== sample.audioPath) return; playAttempt.current++; pendingPlay.current = false; setIsPlaying(false); stopTick(); }}
        onTimeUpdate={(event) => { if (event.currentTarget === audioRef.current && currentPath.current === sample.audioPath) handleTimeUpdate(); }}
        onEnded={(event) => { if (event.currentTarget !== audioRef.current || currentPath.current !== sample.audioPath) return; setIsPlaying(false); stopTick(); onListened(); onEnded?.(); }}
        className="sr-only"
      />

      <div className="tts-gate-row">
        <span className="tts-elapsed">{formatTime(audioRef.current?.currentTime ?? 0)}</span>

        {gateMet ? (
          <span className="tts-gate-ready">Ready to vote</span>
        ) : isPlaying ? (
          <span className="tts-countdown">
            <span className="tts-countdown-num">{gateRemaining}</span>
          </span>
        ) : null}
      </div>

      <div className="tts-gate-track">
        <motion.div
          className={`tts-gate-fill ${gateMet ? "done" : ""}`}
          animate={{ scaleX: gateProgress }}
          transition={{ duration: 0.15, ease: "linear" }}
        />
      </div>
    </motion.div>
  );
});

function formatTime(seconds: number): string {
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
